import jsPDF from 'jspdf';
import type { ScheduleItem } from '@/types';
import { splitTags } from '@/utils/tags';

interface FooterConfig {
    left?: string;
    center?: string;
    right?: string;
}

interface CategoryData {
    name: string;
    responsible?: string;
}

interface SchedulePdfOptions {
    eventName: string;
    eventLocation: string;
    eventDate: string;
    eventTime?: string;
    lastEdited?: string;
    items: (ScheduleItem & { displayStartTime: string; displayEndTime: string })[];
    translations: {
        title: string;
        topic: string;
        duration: string;
        responsible: string;
        note: string;
        time: string;
        minutes: string;
        lastEdited: string;
        categoryFormat: string;
        noAssignment: string;
    };
    footerConfig?: FooterConfig;
    categories?: CategoryData[];
    showDuration?: boolean;
    biggerFooter?: boolean;
}

export const generateSchedulePdf = ({
    eventName,
    eventLocation,
    eventDate,
    eventTime,
    lastEdited,
    items,
    translations,
    footerConfig,
    categories = [],
    showDuration = true,
    biggerFooter = false,
}: SchedulePdfOptions): void => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 12;
    const contentWidth = pageWidth - 2 * margin;
    let yPosition = margin;

    // Base font size
    const baseFontSize = 10;

    // Keep a small safe distance from the physical page edge.
    // Lower value => footer sits closer to the bottom.
    const footerBottomPadding = 4;
    const footerBaselineY = pageHeight - footerBottomPadding;

    // All font sizes relative to base
    const fontSizes = {
        header: baseFontSize * 2,
        eventDetails: baseFontSize * 1.6,
        tableHeader: baseFontSize * 1.4,
        tableContent: baseFontSize * 1.4,
        note: baseFontSize * 1.4,
        footer: baseFontSize * (biggerFooter ? 1.2 : 0.8),
    };

    // Column widths
    const colTime = margin + 5;
    const colTopic = margin + 25;
    const colResponsible = showDuration ? margin + 120 : margin + 135;
    const colDuration = pageWidth - margin - 5;

    // Line heights relative to font sizes
    const lineHeight = baseFontSize * 0.8;

    // Helper function to check if we need a new page
    const checkPageBreak = (requiredHeight: number) => {
        if (yPosition + requiredHeight > footerBaselineY) {
            doc.addPage();
            yPosition = margin;
            return true;
        }
        return false;
    };

    // Header
    doc.setFontSize(fontSizes.header);
    doc.setFont('helvetica', 'bold');

    const headerTextRaw = `${eventName || translations.title} (${eventLocation})`.normalize('NFC');
    // Leave some breathing room; the header is centered so width should be <= contentWidth.
    const headerLines = doc.splitTextToSize(headerTextRaw, contentWidth);

    // Approximate header line height by font size (jsPDF is points-based; this works well in practice)
    const headerLineHeight = fontSizes.header * 0.4;
    const headerBlockHeight = headerLines.length * headerLineHeight + 2;

    // Ensure there's room for the header + date line so they never overlap the footer.
    const eventDetailsBlockHeight = fontSizes.eventDetails * 0.6 + 10;
    checkPageBreak(headerBlockHeight + eventDetailsBlockHeight + 6);

    headerLines.forEach((line: string, idx: number) => {
        doc.text(line, pageWidth / 2, yPosition + idx * headerLineHeight, {
            align: 'center',
            charSpace: 0,
        });
    });

    yPosition += headerLines.length * headerLineHeight;

    // Event details
    doc.setFontSize(fontSizes.eventDetails);
    doc.setFont('helvetica', 'normal');
    const dateStr = `${eventDate}${eventTime ? ` - ${eventTime}` : ''}`;
    doc.text(dateStr, pageWidth / 2, yPosition, { align: 'center', charSpace: 0 });
    yPosition += 4;

    // Table header
    doc.setFillColor(67, 126, 234); // Blue background
    doc.rect(margin, yPosition, contentWidth, 8, 'F');

    doc.setTextColor(255, 255, 255);
    doc.setFontSize(fontSizes.tableHeader);
    doc.setFont('helvetica', 'bold');

    doc.text(translations.time, colTime, yPosition + 6, { charSpace: 0 });
    doc.text(translations.topic, colTopic, yPosition + 6, { charSpace: 0 });
    doc.text(translations.responsible, colResponsible, yPosition + 6, { charSpace: 0 });
    if (showDuration) {
        doc.text(translations.duration, colDuration, yPosition + 6, {
            align: 'right',
            charSpace: 0,
        });
    }

    yPosition += 10;
    doc.setTextColor(0, 0, 0);

    // Table rows
    items.forEach((item, index) => {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(fontSizes.tableContent);

        const tags = splitTags(item.tags);
        const titleWithTags = tags.length > 0 ? `${tags.join(', ')} - ${item.title}` : item.title;

        // Normalize text to handle special characters properly
        const normalizedTitle = titleWithTags.normalize('NFC');
        const normalizedResponsible = item.responsible?.normalize('NFC') || '';
        const normalizedNote = item.note?.normalize('NFC') || '';

        // Calculate row height based on relative line height
        // Adjust text widths based on whether duration is shown
        const topicWidth = showDuration ? 85 : 100;
        const responsibleWidth = showDuration ? 45 : 60;

        const topicLines = doc.splitTextToSize(normalizedTitle, topicWidth);
        const responsibleLines = normalizedResponsible
            ? doc.splitTextToSize(normalizedResponsible, responsibleWidth)
            : [];
        const noteLines = normalizedNote
            ? doc.splitTextToSize(normalizedNote, contentWidth - 10)
            : [];

        const mainRowHeight = Math.max(
            topicLines.length * lineHeight,
            responsibleLines.length * lineHeight,
            8
        );
        const noteRowHeight = noteLines.length > 0 ? noteLines.length * lineHeight * 0.8 + 3 : 0;
        const totalRowHeight = mainRowHeight + noteRowHeight + 3;

        // Check if we need a new page
        checkPageBreak(totalRowHeight);

        // Alternating row colors
        if (index % 2 === 0) {
            doc.setFillColor(248, 249, 250);
            doc.rect(margin, yPosition - 2, contentWidth, mainRowHeight + 2, 'F');
        }

        // Time
        const timeText = item.displayStartTime || '';
        doc.text(timeText, colTime, yPosition + 5, { charSpace: 0 });

        // Topic (multiline) with highlight
        topicLines.forEach((line: string, i: number) => {
            const lineY = yPosition + 5 + i * lineHeight;
            if (item.highlightTitle) {
                doc.setFillColor(255, 235, 59); // Yellow highlight
                const lineWidth = doc.getTextWidth(line) + 2;
                doc.rect(colTopic - 1, lineY - 6, lineWidth, lineHeight, 'F');
            }
            doc.text(line, colTopic, lineY, { charSpace: 0 });
        });

        // Responsible (multiline) with highlight - moved before duration
        if (item.responsible) {
            responsibleLines.forEach((line: string, i: number) => {
                const lineY = yPosition + 5 + i * lineHeight;
                if (item.highlightResponsible) {
                    doc.setFillColor(255, 235, 59); // Yellow highlight
                    const lineWidth = doc.getTextWidth(line) + 2;
                    doc.rect(colResponsible - 1, lineY - 6, lineWidth, lineHeight, 'F');
                }
                doc.text(line, colResponsible, lineY, { charSpace: 0 });
            });
        }

        // Duration
        if (showDuration) {
            const durationText = item.duration ? `${item.duration} ${translations.minutes}` : '';
            doc.text(durationText, colDuration, yPosition + 5, { align: 'right', charSpace: 0 });
        }

        yPosition += mainRowHeight;

        // Note
        if (noteLines.length > 0) {
            if (index % 2 === 0) {
                doc.setFillColor(248, 249, 250);
                doc.rect(margin, yPosition, contentWidth, noteRowHeight, 'F');
            }

            doc.setFont('helvetica', 'italic');
            doc.setFontSize(fontSizes.note);
            doc.setTextColor(100, 100, 100);

            // Add highlight for note if marked
            noteLines.forEach((line: string, i: number) => {
                const lineY = yPosition + 7 + i * lineHeight * 0.8;
                if (item.highlightNote) {
                    doc.setFillColor(255, 235, 59); // Yellow highlight
                    const lineWidth = doc.getTextWidth(line) + 2;
                    doc.rect(
                        margin + 4,
                        lineY - 5,
                        Math.min(lineWidth, contentWidth - margin),
                        lineHeight * 0.8,
                        'F'
                    );
                }
                doc.text(line, margin + 5, lineY, { charSpace: 0 });
            });

            doc.setTextColor(0, 0, 0);

            yPosition += noteRowHeight;
        }

        yPosition += 3; // Row spacing
    });

    // Footer with page numbers and last edited
    const pageCount = doc.getNumberOfPages();

    // Helper function to replace placeholders in footer text
    const replaceFooterPlaceholders = (text: string, pageNum: number): string => {
        let result = text;

        // Replace {{last change}} placeholder
        if (lastEdited) {
            result = result.replace(
                /{{last change}}/gi,
                `${translations.lastEdited}: ${lastEdited}`
            );
        } else {
            result = result.replace(/{{last change}}/gi, '');
        }

        // Replace {{page}} placeholder
        result = result.replace(/{{page}}/gi, `${pageNum} / ${pageCount}`);

        // Replace {{category:CategoryName}} placeholders
        const categoryRegex = /{{category:([^}]+)}}/gi;
        result = result.replace(categoryRegex, (match, categoryName) => {
            const trimmedName = categoryName.trim();
            const category = categories.find(
                (cat) => cat.name.toLowerCase() === trimmedName.toLowerCase()
            );

            if (!category) {
                return match; // Keep original if category not found
            }

            return translations.categoryFormat
                .replace('{category}', category.name)
                .replace('{responsible}', category.responsible || translations.noAssignment);
        });

        return result.trim();
    };

    for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(fontSizes.footer);
        doc.setFont('helvetica', 'normal');
        if (biggerFooter) {
            doc.setTextColor(0, 0, 0);
        } else {
            doc.setTextColor(150, 150, 150);
        }

        // Left footer
        if (footerConfig?.left) {
            const leftText = replaceFooterPlaceholders(footerConfig.left, i);
            if (leftText) {
                doc.text(leftText, margin, footerBaselineY, { charSpace: 0 });
            }
        }

        // Center footer
        if (footerConfig?.center) {
            const centerText = replaceFooterPlaceholders(footerConfig.center, i);
            if (centerText) {
                doc.text(centerText, pageWidth / 2, footerBaselineY, {
                    align: 'center',
                    charSpace: 0,
                });
            }
        }

        // Right footer
        if (footerConfig?.right) {
            const rightText = replaceFooterPlaceholders(footerConfig.right, i);
            if (rightText) {
                doc.text(rightText, pageWidth - margin, footerBaselineY, {
                    align: 'right',
                    charSpace: 0,
                });
            }
        }
    }

    // Generate filename
    const filename = `${translations.title.replace(/\s+/g, '_')}_${eventDate.replace(/[^\d]/g, '')}.pdf`;

    // Download
    doc.save(filename);
};
